import chunk from 'lodash/chunk';

/**
 * How the bundle cover grid in the hit card popup (606PopupHitCardBundleCoverGrid) lays a
 * bundle's packshots out across its slides.
 *
 * Every slide is a 2x2 grid of TILES_PER_SLIDE tiles (bar the single cover case, rule 0), and every
 * tile is one of:
 *   - 'cover' a packshot of one of the bundle's items
 *   - 'blank' black filler, so a slide never has a hole in it
 *   - 'more'  "+ More": only ever the last tile of the last slide, and only when the bundle has
 *             items that aren't on any slide
 *
 * The rules, in order:
 *   0. A bundle with only one cover shows it full size, on a slide of its own: one quarter-size
 *      packshot and three blanks would look broken.
 *   1. Fill as many whole slides with covers as there are covers for.
 *   2. The covers left over after the last whole slide only get a slide of their own if it would
 *      need no more than MAX_BLANK_TILES blanks. Otherwise they're left off and fall under
 *      "+ More": a slide that's mostly black looks broken. (A bundle with too few covers to fill
 *      one slide always gets its slide, blanks and all, as there's nothing else to show.)
 *   3. Never more than MAX_SLIDES slides. Anything past them falls under "+ More".
 *   4. If anything isn't shown, the last tile of the last slide becomes "+ More", which pushes
 *      that slide's last cover off too.
 *
 *   covers | layout ( _ blank, + "+ More")
 *   -------|--------------------------------------------
 *   1      | [1]                             rule 0: full size
 *   2      | [1 2 _ _]
 *   3      | [1 2 3 _]
 *   4      | [1 2 3 4]
 *   5      | [1 2 3 +]                       rule 2: a [5 _ _ _] slide would be mostly black
 *   6      | [1 2 3 +]
 *   7      | [1 2 3 4] [5 6 7 _]             rule 2: one blank is fine
 *   8      | [1 2 3 4] [5 6 7 8]
 *   9      | [1 2 3 4] [5 6 7 +]
 *   30     | [1-4] [5-8] [9-12] [13 14 15 +] rule 3
 *
 * A bundle can have more items than covers (bundle_covers drops items that share a cover), so
 * itemCount is the bundle's real size, used only to decide whether "+ More" is needed.
 */

export const TILES_PER_SLIDE = 4;

// Some bundles have 80+ items. Of the game bundles on sale when this test was written (2-30 items,
// most around 17), going past 4 slides would have shown every item for 1 more bundle in 19, while
// loading up to 8 more images per hover. The "+ More" tile links to the product page for the rest.
export const MAX_SLIDES = 4;

export const MAX_BLANK_TILES = 1;

/**
 * @param  {Array.Object} covers      The bundle's bundle_covers
 * @param  {Number}       [itemCount] How many items the bundle has, if more than it has covers
 * @return {Array.Array.Object}       One array of tiles per slide
 */
export const buildBundleGridSlides = (covers = [], itemCount = 0) => {
  if (covers.length === 0) return [];

  // Rule 0
  if (covers.length === 1) return [[{ type: 'cover', cover: covers[0] }]];

  // Rules 1 and 2
  const wholeSlides = Math.floor(covers.length / TILES_PER_SLIDE);
  const leftover = covers.length % TILES_PER_SLIDE;
  const leftoverGetsSlide = leftover > 0 &&
    (wholeSlides === 0 || TILES_PER_SLIDE - leftover <= MAX_BLANK_TILES);

  // Rule 3
  const slideCount = Math.min(wholeSlides + (leftoverGetsSlide ? 1 : 0), MAX_SLIDES);
  const tileCount = slideCount * TILES_PER_SLIDE;

  // Rule 4
  let shownCount = Math.min(covers.length, tileCount);
  const hasMore = Math.max(itemCount, covers.length) > shownCount;
  if (hasMore && shownCount === tileCount) shownCount -= 1;

  const coverTiles = covers.slice(0, shownCount).map(cover => ({ type: 'cover', cover }));
  const moreTiles = hasMore ? [{ type: 'more' }] : [];
  const blankTiles = Array(tileCount - coverTiles.length - moreTiles.length).fill({ type: 'blank' });

  return chunk([...coverTiles, ...blankTiles, ...moreTiles], TILES_PER_SLIDE);
};
